import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { useRef, useState } from "react";

export const ScrollButton = ({
  direction,
  onClick,
  className = "",
}: {
  direction: "left" | "right";
  onClick: () => void;
  className?: string;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "absolute top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-action-tertiary shadow-lg",
      "flex items-center justify-center transition-all duration-200",
      "hover:scale-110 hover:bg-action-tertiary-hover",
      "disabled:opacity-0 disabled:pointer-events-none",
      direction === "left" ? "-left-2" : "-right-2",
      className,
    )}
  >
    {direction === "left" ? (
      <div className="w-3 h-3 text-white rotate-90">{arrow}</div>
    ) : (
      <div className="w-3 h-3 text-white -rotate-90">{arrow}</div>
    )}
  </button>
);

export const ScrollContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const handleScroll = () => {
    const scrollContainer = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (scrollContainer) {
      const scrollLeft = Math.round(scrollContainer.scrollLeft);
      const scrollWidth = Math.round(scrollContainer.scrollWidth);
      const clientWidth = Math.round(scrollContainer.clientWidth);

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // Added 1px threshold
    }
  };

  const scroll = (direction: "left" | "right") => {
    const scrollContainer = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (scrollContainer) {
      const scrollAmount = 700;
      scrollContainer.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);

      // Create and attach ResizeObserver
      resizeObserverRef.current = new ResizeObserver(() => {
        handleScroll();
      });

      resizeObserverRef.current.observe(scrollContainer);
      handleScroll(); // Check initial state

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }
  }, [children]);

  return (
    <div className="relative group">
      <ScrollArea ref={scrollContainerRef} className="relative flex gap-y-2">
        <div className="flex flex-row gap-x-4 pb-4">{children}</div>
        <ScrollBar
          orientation="horizontal"
          className="overflow-y-hidden cursor-grab bg-background"
          forceMount={true}
          scrollBarColor="bg-panel-background"
        />
      </ScrollArea>
      <div
        className={cn(
          "absolute right-0 top-0 bottom-4 w-[146px] pointer-events-none z-10",
          !canScrollRight ? "hidden" : "hidden touch-device:flex",
        )}
        style={{
          background:
            "linear-gradient(90deg, rgba(26, 30, 35, 0.00) 0%, rgba(26, 30, 35, 0.63) 46.29%, #1A1E23 94.44%)",
        }}
      />
      <div
        className={cn(
          "absolute left-0 top-0 bottom-4 w-[146px] pointer-events-none z-10",
          !canScrollLeft ? "hidden" : "hidden touch-device:flex",
        )}
        style={{
          background:
            "linear-gradient(270deg, rgba(26, 30, 35, 0.00) 0%, rgba(26, 30, 35, 0.63) 46.29%, #1A1E23 94.44%)",
        }}
      />
      <ScrollButton
        direction="left"
        onClick={() => scroll("left")}
        className={cn(
          "hidden touch-device:flex",
          !canScrollLeft ? "opacity-0" : "opacity-100",
        )}
      />
      <ScrollButton
        direction="right"
        onClick={() => scroll("right")}
        className={cn(
          "hidden touch-device:flex",
          !canScrollRight ? "opacity-0" : "opacity-100",
        )}
      />
    </div>
  );
};
